import { useEffect } from "react";
import { useTheme } from "../context/ThemeContext";

// Apply the same overlay sweep effect used by banner to section headings
// and trigger occasional glitch-once animations on scroll
const HeadingAnimator = () => {
  const { isMatrixEnabled } = useTheme();

  useEffect(() => {
    const root = document.querySelector('.home') || document.body;
    if (!root) return;

    const headings = Array.from(root.querySelectorAll('[data-section] h2, [data-section] h3'));

    // Prepare each heading with banner-like structure: overlay + span-wrapped text
    headings.forEach((heading) => {
      if (!heading.classList.contains('banner-animated-heading')) {
        heading.classList.add('banner-animated-heading');
      }
      // If overlay not present, insert it as first child
      if (!heading.querySelector('.banner-overlay')) {
        const overlay = document.createElement('div');
        overlay.className = 'banner-overlay';
        heading.insertBefore(overlay, heading.firstChild);
      }
      // Ensure text is wrapped in a span for opacity reveal
      const hasSpan = Array.from(heading.childNodes).some(
        (n) => n.nodeType === Node.ELEMENT_NODE && (n).classList && (n).classList.contains('banner-overlay') === false && (n).tagName === 'SPAN'
      );
      if (!hasSpan) {
        const span = document.createElement('span');
        const nodesToWrap = [];
        heading.childNodes.forEach((n) => {
          if (!(n.nodeType === Node.ELEMENT_NODE && (n).classList && (n).classList.contains('banner-overlay'))) {
            nodesToWrap.push(n);
          }
        });
        nodesToWrap.forEach((n) => span.appendChild(n));
        heading.appendChild(span);
      }
      // Set data-text for glitch pseudo-elements
      heading.setAttribute('data-text', heading.textContent || '');
    });

    // Reveal on intersection, then show text
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const el = entry.target;
          if (entry.isIntersecting) {
            el.classList.add('in-view');
            // Delay text reveal to sync with overlay sweep
            setTimeout(() => {
              const span = el.querySelector('span');
              if (span) el.classList.add('show-text');
            }, 300);
          }
        });
      },
      { threshold: 0.5 }
    );
    headings.forEach((h) => io.observe(h));

    // Occasionally trigger a short glitch on scroll for in-view headings (Matrix only)
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        if (!isMatrixEnabled) { ticking = false; return; }
        const viewportHeight = window.innerHeight;
        const candidates = headings.filter((h) => {
          const rect = h.getBoundingClientRect();
          return rect.top < viewportHeight * 0.9 && rect.bottom > viewportHeight * 0.1;
        });
        if (candidates.length && Math.random() < 0.12) {
          const target = candidates[Math.floor(Math.random() * candidates.length)];
          target.classList.add('glitch-once');
          setTimeout(() => target.classList.remove('glitch-once'), 380);
        }
        ticking = false;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });

    return () => {
      io.disconnect();
      window.removeEventListener('scroll', onScroll);
    };
  }, [isMatrixEnabled]);

  return null;
};

export default HeadingAnimator;

